import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Hero from '@/components/Hero';
import Footer from '@/components/Footer';
import { industries } from '@/data/industries';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { 
  GlassWater, Croissant, Beef, Droplet, 
  Utensils, Apple, Package, ArrowRight, Users, Briefcase
} from 'lucide-react';
import companyHero from '@/assets/company-hero-new.png';
import careerHero from '@/assets/career-hero.jpg';
import tftLogo from '@/assets/tft-logo.png';
import milkCartonIcon from '@/assets/icons/milk-carton.png';
import milkCartonHoverIcon from '@/assets/icons/milk-carton-hover.png';
import spreadsBowlIcon from '@/assets/icons/spreads-bowl.png';
import spreadsBowlHoverIcon from '@/assets/icons/spreads-bowl-hover.png';
import glassBottleIcon from '@/assets/icons/glass-bottle.png';
import glassBottleHoverIcon from '@/assets/icons/glass-bottle-hover.png';
import { useLanguage } from '@/contexts/LanguageContext';
import { useState } from 'react';
import { HomepageCarousel } from '@/components/HomepageCarousel';
import { NewsCarousel } from '@/components/NewsCarousel';
import milkImage from '@/assets/dairy-products/milk-cartons.jpg';
import yoghurtImage from '@/assets/dairy-products/yoghurt.jpg';
import cheeseImage from '@/assets/dairy-products/cheese.jpg';
import butterImage from '@/assets/dairy-products/butter.jpg';
import iceCreamImage from '@/assets/dairy-products/ice-cream.jpg';
import beerImage from '@/assets/beverages/beer.jpg';
import juicesImage from '@/assets/beverages/juices.jpg';
import softDrinksImage from '@/assets/beverages/soft-drinks.jpg';
import waterImage from '@/assets/beverages/water.jpg';
import wineImage from '@/assets/beverages/wine.jpg';
import eggsImage from '@/assets/meat-fish/eggs.jpg';
import meatFishImage from '@/assets/meat-fish/meat-fish.jpg';
import oilsImage from '@/assets/oils-fats/oils.jpg';
import fatsImage from '@/assets/oils-fats/fats.jpg';

const iconMap = {
  milk: milkCartonIcon,
  'glass-bottle': glassBottleIcon,
  croissant: Croissant,
  beef: Beef,
  droplet: Droplet,
  utensils: Utensils,
  apple: Apple,
  package: Package,
  'spreads-bowl': spreadsBowlIcon
};

const Index = () => {
  const { language, t } = useLanguage();
  const [hoveredIndustry, setHoveredIndustry] = useState<string | null>(null);

  const allCategories = [
    // Dairy Products
    { 
      id: 'milk', 
      slug: 'milk', 
      industrySlug: 'dairy',
      title: { el: 'Γάλα', en: 'Milk' },
      image: milkImage
    },
    { 
      id: 'yoghurt', 
      slug: 'yoghurt', 
      industrySlug: 'dairy',
      title: { el: 'Γιαούρτι', en: 'Yoghurt' },
      image: yoghurtImage
    },
    { 
      id: 'cheese', 
      slug: 'cheese', 
      industrySlug: 'dairy',
      title: { el: 'Τυρί', en: 'Cheese' },
      image: cheeseImage
    },
    { 
      id: 'butter', 
      slug: 'butter', 
      industrySlug: 'dairy',
      title: { el: 'Βούτυρο', en: 'Butter' },
      image: butterImage
    },
    { 
      id: 'ice-cream', 
      slug: 'ice-cream', 
      industrySlug: 'dairy',
      title: { el: 'Παγωτό', en: 'Ice Cream' },
      image: iceCreamImage
    },
    // Beverages
    { 
      id: 'water', 
      slug: 'water', 
      industrySlug: 'beverage',
      title: { el: 'Νερό', en: 'Water' },
      image: waterImage
    },
    { 
      id: 'soft-drinks', 
      slug: 'soft-drinks', 
      industrySlug: 'beverage',
      title: { el: 'Αναψυκτικά', en: 'Soft Drinks' },
      image: softDrinksImage
    },
    { 
      id: 'juices', 
      slug: 'juices', 
      industrySlug: 'beverage',
      title: { el: 'Χυμοί', en: 'Juices' },
      image: juicesImage
    },
    { 
      id: 'beer', 
      slug: 'beer', 
      industrySlug: 'beverage',
      title: { el: 'Μπύρα', en: 'Beer' },
      image: beerImage
    },
    { 
      id: 'wine', 
      slug: 'wine', 
      industrySlug: 'beverage',
      title: { el: 'Οίνος', en: 'Wine' },
      image: wineImage
    },
    // Meat & Fish
    { 
      id: 'meat-fish', 
      slug: 'meat-fish', 
      industrySlug: 'meat-fish-eggs',
      title: { el: 'Κρέας & Ψάρι', en: 'Meat & Fish' },
      image: meatFishImage
    },
    { 
      id: 'eggs', 
      slug: 'eggs', 
      industrySlug: 'meat-fish-eggs',
      title: { el: 'Αυγά', en: 'Eggs' },
      image: eggsImage
    },
    // Oils & Fats
    { 
      id: 'oils', 
      slug: 'oils', 
      industrySlug: 'oils-fats',
      title: { el: 'Έλαια', en: 'Oils' },
      image: oilsImage
    },
    { 
      id: 'fats', 
      slug: 'fats', 
      industrySlug: 'oils-fats',
      title: { el: 'Λίπη', en: 'Fats' },
      image: fatsImage
    }
  ];

  return (
    <div className="min-h-screen flex flex-col">
      <SEO 
        title={t('Αρχική', 'Home')}
        description={t('Η TFT προσφέρει ολοκληρωμένες λύσεις επεξεργασίας και συσκευασίας για τη βιομηχανία τροφίμων και ποτών. 60+ χρόνια εμπειρίας.', 'TFT offers complete food processing and packaging solutions for the food & beverage industry. 60+ years of experience.')}
        canonical="https://www.tft.gr/"
      />
      <Navigation />
      <Hero />

      <main>
        {/* Industries Section */}
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">{t('Κλάδοι Δραστηριότητας', 'Industries')}</h2>
              <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
                {t('Εξυπηρετούμε όλους τους κλάδους της βιομηχανίας τροφίμων και ποτών με ολοκληρωμένες λύσεις', 'Serving all sectors of the food and beverage industry with complete solutions')}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {industries.map((industry) => {
                const Icon = iconMap[industry.icon as keyof typeof iconMap];
                const isImage = typeof Icon === 'string';
                const isMilkCarton = industry.icon === 'milk';
                const isSpreadsBowl = industry.icon === 'spreads-bowl';
                const isGlassBottle = industry.icon === 'glass-bottle';
                const isHovered = hoveredIndustry === industry.id;
                return (
                  <Link key={industry.id} to={`/industries/${industry.slug}`}>
                    <Card 
                      className="h-full hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
                      onMouseEnter={() => setHoveredIndustry(industry.id)}
                      onMouseLeave={() => setHoveredIndustry(null)}
                    >
                      <CardContent className="p-6">
                        <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                          {isImage ? (
                            <img 
                              src={
                                isMilkCarton && isHovered ? milkCartonHoverIcon : 
                                isSpreadsBowl && isHovered ? spreadsBowlHoverIcon :
                                isGlassBottle && isHovered ? glassBottleHoverIcon :
                                Icon
                              } 
                              alt={`${industry.title[language]} icon`} 
                              className="h-10 w-10 transition-all duration-300"
                              width="40"
                              height="40"
                              loading="lazy"
                            />
                          ) : (
                            <Icon className="h-8 w-8 text-primary group-hover:text-primary-foreground" />
                          )}
                        </div>
                        <h3 className="text-xl font-semibold mb-2 group-hover:text-primary transition-colors">
                          {industry.title[language]}
                        </h3>
                        <p className="text-muted-foreground text-sm">
                          {industry.description[language]}
                        </p>
                      </CardContent>
                    </Card>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>

        {/* Featured Products Carousel */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl md:text-4xl font-bold mb-4 md:mb-6">{t('Επιλεγμένα Προϊόντα', 'Featured Products')}</h2>
              <p className="text-base md:text-xl text-muted-foreground max-w-3xl mx-auto">
                {t('Ανακαλύψτε τις ολοκληρωμένες λύσεις μας για όλους τους κλάδους', 'Discover our complete solutions for all industries')}
              </p>
            </div>
            <HomepageCarousel 
              categories={allCategories}
              language={language}
              t={t}
            />
          </div>
        </section>

        {/* About & Careers Section */}
        <section className="py-20 bg-muted">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* About Card */}
              <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={companyHero} 
                    alt="Εταιρεία" 
                    className="w-full h-full object-cover object-[center_60%] group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-8">
                  <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                    <Briefcase className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t('Η Εταιρεία μας', 'Our Company')}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('Με πάνω από 60 χρόνια εμπειρίας στον κλάδο της επεξεργασίας και συσκευασίας τροφίμων, η TFT προσφέρει ολοκληρωμένες λύσεις στις μεγαλύτερες βιομηχανίες της Ελλάδας και των Βαλκανίων.', 'With over 60 years of experience in food processing and packaging, TFT offers complete solutions to the largest industries in Greece and the Balkans.')}
                  </p>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/company">
                      {t('Μάθετε Περισσότερα', 'Learn More')} <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>

              {/* Careers Card */}
              <Card className="overflow-hidden group hover:shadow-xl transition-all duration-300">
                <div className="aspect-video overflow-hidden">
                  <img 
                    src={careerHero} 
                    alt="Καριέρα" 
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardContent className="p-8">
                  <div className="mb-4 p-3 bg-primary/10 rounded-xl w-fit">
                    <Users className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold mb-4">{t('Καριέρα', 'Careers')}</h3>
                  <p className="text-muted-foreground mb-6">
                    {t('Γίνετε μέλος της ομάδας μας και συμβάλλετε στην ανάπτυξη της βιομηχανίας τροφίμων. Αναζητούμε ταλαντούχους επαγγελματίες με πάθος για την τεχνολογία και την καινοτομία.', 'Join our team and contribute to the development of the food industry. We are looking for talented professionals with a passion for technology and innovation.')}
                  </p>
                  <Button variant="outline" size="lg" asChild>
                    <Link to="/company/careers">
                      {t('Δείτε Θέσεις', 'View Positions')} <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* News Carousel */}
        <NewsCarousel />
      </main>

      <Footer />
    </div>
  );
};

export default Index;